import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { CommonService, GoogleMapService } from 'src/app/core/services';
import { ShippingService } from 'src/app/shipping/services';
import {
  AppointmentSchedulerConfirmationType,
  CustomerLocation,
  defaultPickupStop,
  Place,
  PlaceTypeEnum,
  State,
  UnitOfMeasure,
} from '../../models';
import { OrderEntryFormEditStatus } from '../../models/order-entry-form';
import { OrderEntryFormBookedEditEnum } from '../../models/order-entry-form-booked-edit-enum';
import { StopTypes } from '../../models/stop-types';
import { BaseComponent } from '../base-component';

@Component({
  selector: 'kbxl-load-stop-form',
  templateUrl: './load-stop-form.component.html',
  styleUrls: ['./load-stop-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class LoadStopFormComponent extends BaseComponent implements OnChanges, OnInit {
  @Input() form: UntypedFormGroup;
  @Input() index: number;
  @Input() includeDelete: boolean;
  @Input() schedulerConfirmationTypes: AppointmentSchedulerConfirmationType[];
  @Input() loadingSchedulerConfirmationTypes: boolean;
  @Input() states: State[];
  @Input() loadingStates: boolean;
  @Input() unitsOfMeasure: UnitOfMeasure[];
  @Input() loadingUnitsOfMeasure: boolean;
  @Input() pickupStopNumbers: { stopNbr: number }[];
  @Input() orderEntryEditStatus: OrderEntryFormEditStatus;

  @Output() addLoadStop = new EventEmitter<number>();
  @Output() changeLoadStopType = new EventEmitter<number>();
  @Output() deleteLoadStop = new EventEmitter<number>();

  suggestions: Place[];
  locationSuggestions: CustomerLocation[] = [];
  displayAddressLine2 = false;
  displayAddressLine3 = false;
  disableForLineItemsOnly: boolean;

  pickupStopType = StopTypes[StopTypes.Pickup];
  deliveryStopType = StopTypes[StopTypes.Delivery];
  constructor(
    private fb: UntypedFormBuilder,
    private confirmationService: ConfirmationService,
    private googleService: GoogleMapService,
    private shippingService: ShippingService,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService
  ) {
    super();
  }
  ngOnInit(): void {
    // HACK in primeNG to get around issue for RadioButton Groups: https://github.com/primefaces/primeng/issues/9162
    this.form
      .get('stopType')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.form.controls['stopType'].setValue(val, { emitEvent: false });
      });

    this.form
      .get('isLive')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.form.controls['isLive'].setValue(val, { emitEvent: false });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.form) {
        const address2 = this.form.controls['address2'].value;
        if (address2 && address2.length > 0) {
          this.displayAddressLine2 = true;
        }
        const address3 = this.form.controls['address3'].value;
        if (address3 && address3.length > 0) {
          this.displayAddressLine3 = true;
        }
      }
    }

    if (changes.orderEntryEditStatus) {
      if (this.orderEntryEditStatus) {
        this.restrictControlsBasedOnLoadStatusForEditMode(this.orderEntryEditStatus);
      } else {
        this.disableForLineItemsOnly = false;
      }
    }
  }

  get contacts() {
    return this.form.controls['contacts'] as UntypedFormArray;
  }

  get stopNbr() {
    return this.form.controls['stopNbr'].value;
  }

  get stopType() {
    return this.form.controls['stopType'].value;
  }

  get city() {
    return this.form.controls['city'].value;
  }

  get state() {
    return this.form.controls['state'].value;
  }

  get stopTypeTitle() {
    let title = `${this.stopNbr}. ${this.stopType} Stop`;
    if (this.city && this.city.length > 0 && this.state && this.state.abbreviation) {
      title += ` (${this.city}, ${this.state.abbreviation})`;
    }

    return title;
  }

  get countries() {
    return this.commonService.getCountries();
  }

  get lineItems() {
    return this.form.controls['lineItems'] as UntypedFormArray;
  }

  restrictControlsBasedOnLoadStatusForEditMode(status: OrderEntryFormEditStatus) {
    if (status.isBooked && status.loadEditRestrictions === OrderEntryFormBookedEditEnum.LineItemsOnly) {
      this.form.disable();
      this.form.controls['lineItems'].enable();
      this.disableForLineItemsOnly = true;
    } else {
      this.disableForLineItemsOnly = false;
    }
  }
  addContact() {
    this.contacts.push(
      this.fb.group({
        firstName: '',
        lastName: '',
        position: '',
        phoneNumber: '',
        email: '',
      })
    );
  }

  deleteContact(index: number) {
    this.contacts.removeAt(index);
  }

  addLineItem() {
    this.lineItems.push(
      this.fb.group({
        loadLineItemNumber: null,
        quantity: null,
        unitOfMeasure: null,
        weight: null,
        customerPurchaseOrder: null,
        pickupStopNumber: defaultPickupStop.stopNbr,
        deliveryStopNumber: this.stopNbr,
      })
    );
  }

  addStop() {
    this.addLoadStop.emit(this.index);
  }

  changeStopType() {
    this.changeLoadStopType.emit(this.index);
  }

  deleteLineItem(index: number) {
    this.lineItems.removeAt(index);
  }

  deleteStop() {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove this stop?<br/> ${this.stopTypeTitle}`,
      accept: () => {
        this.deleteLoadStop.emit(this.stopNbr);
      },
    });
  }

  addAddressLine() {
    if (!this.displayAddressLine2) {
      this.displayAddressLine2 = true;
    } else if (!this.displayAddressLine3) {
      this.displayAddressLine3 = true;
    }
  }

  citySearch(event: any) {
    this.googleService.addressAutoComplete(event.query).then((x) => {
      this.suggestions = x.map((y) => {
        if (y) {
          if (!y.stateAbbrev) {
            y.stateAbbrev = this.getStateAbbrev(y.state);
          }
          if (!y.state) {
            y.state = this.getState(y.stateAbbrev);
          }
        }

        return y;
      });
    });
  }

  populateAddress(place: Place) {
    this.populatePlace(place).then((x) => {
      this.form.patchValue({ address1: x.address });
      this.form.patchValue({ city: x.city });
      this.form.patchValue({ state: { abbreviation: x.stateAbbrev, name: x.state } });
      this.form.patchValue({ postalCode: x.postalCode });
      this.form.patchValue({ country: { id: this.getCountryAbbrev(x.country), name: x.country } });
      this.form.patchValue({ latitude: x.latitude });
      this.form.patchValue({ longitude: x.longitude });

      this.form.patchValue({ addressAutoComplete: { address: x.address } });
    });
  }

  addressBlur(location: CustomerLocation) {
    const address: string = this.form.controls['addressAutoComplete'].value;
    if (address != null && address.length > 0 && !address.startsWith('{')) {
      this.form.patchValue({ address1: address });
    }
  }

  private populatePlace(place: Place): Promise<Place> {
    return this.googleService.geocodePlace(place.description).then((x) => {
      if (place.placeType === PlaceTypeEnum.Establistment) {
        place.address = x.address;
        place.postalCode = x.postalCode;
        place.placeType = PlaceTypeEnum.Address;
      }

      if (place.placeType === PlaceTypeEnum.Route) {
        if (x.address) {
          place.address = x.address;
          place.placeType = PlaceTypeEnum.Address;
        } else {
          place.address = null;
          place.placeType = PlaceTypeEnum.City;
        }
      } else if (x.postalCode) {
        place.postalCode = x.postalCode;
      }

      if (x.latitude) {
        place.latitude = x.latitude;
      }
      if (x.longitude) {
        place.longitude = x.longitude;
      }

      return place;
    });
  }

  getStateAbbrev(stateName: string): string {
    if (!stateName) {
      return '';
    }
    if (!this.states) {
      return stateName;
    }
    const state = this.states.find((x) => x.name.toLowerCase() === stateName.toLowerCase());
    return state ? state.abbreviation : '';
  }

  getState(stateAbbrev: string): string {
    if (!stateAbbrev) {
      return '';
    }
    if (!this.states) {
      return stateAbbrev;
    }
    const state = this.states.find((x) => x.abbreviation.toLowerCase() === stateAbbrev.toLowerCase());
    return state ? state.name : '';
  }

  getCountry(countryAbbrev: string): string {
    if (!countryAbbrev) {
      return '';
    }
    if (!this.countries) {
      return countryAbbrev;
    }
    const country = this.countries.find((x) => x.id.toLowerCase() === countryAbbrev.toLowerCase());
    return country ? country.name : '';
  }

  getCountryAbbrev(countryName: string): string {
    if (!countryName) {
      return '';
    }
    if (!this.countries) {
      return countryName;
    }
    const country = this.countries.find((x) => x.name.toLowerCase() === countryName.toLowerCase());
    return country ? country.id : '';
  }

  locationSearch(event: any) {
    this.shippingService
      .searchLocations(event.query)
      .toPromise()
      .then((x) => {
        this.locationSuggestions = x;
        this.cdr.detectChanges();
      });
  }

  populateAddressFromLocation(location: CustomerLocation) {
    if (location) {
      this.form.patchValue({ locationId: location.locationId });
      this.form.patchValue({ locationName: location.locationName });
      this.form.patchValue({ address1: location.address1 });
      this.form.patchValue({ address2: location.address2 });
      this.form.patchValue({ address3: location.address3 });
      this.form.patchValue({ city: location.city });
      this.form.patchValue({ state: { abbreviation: location.state, name: this.getState(location.state) } });
      this.form.patchValue({ postalCode: location.postalCode });
      this.form.patchValue({ country: { id: location.country, name: this.getCountry(location.country) } });
      this.form.patchValue({ latitude: location.latitude });
      this.form.patchValue({ longitude: location.longitude });
      this.form.patchValue({ schedulerConfirmationType: location.appointmentSchedulerConfirmationTypeId });

      this.form.patchValue({ addressAutoComplete: { address: location.address1 } });
    }
  }

  populateToField() {
    const earlyDate = this.form.controls['earlyDate'].value;
    const earlyTime = this.form.controls['earlyTime'].value;

    const lateDate = this.form.controls['lateDate'].value;
    const lateTime = this.form.controls['lateTime'].value;

    if (earlyDate && !lateDate) {
      this.form.controls['lateDate'].setValue(earlyDate);
    }

    if (earlyTime && !lateTime) {
      this.form.controls['lateTime'].setValue(earlyTime);
    }
  }

  updateLateDtTm(): void {
    if (
      this.form.value.lateDate &&
      this.form.value.lateDate.toString().length > 0 &&
      this.form.value.lateTime &&
      this.form.value.lateTime.length > 0
    ) {
      this.form.patchValue({
        lateDtTm: '', // this is because we only set this value on the server; however we need both values to validate the control
      });
    }
  }
}
